import { Button, Collapse, Divider, Spin, Typography } from 'antd';
import useDeclarations from 'hooks/useDeclarations';
import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { transformAgentPath, transformDangerousGoodsPath } from 'views/declarations/uk/export/utils';
import { groupBy, isEmpty } from 'lodash';
import {
    CdsExportNotification,
    CdsExportNotificationAdditionalInformation,
    CdsExportNotificationError,
} from 'store/declarations/uk/cds-declaration';
import parseValidationErrors from 'views/declarations/uk/parseValidationErrors';
import { getFieldName } from 'utils/validationParserUtils';
import { ButtonText, DetailsGrid, Error, MainGrid } from '../declaration-errors.styles';
import { getLastDateOfSubmit } from '../ens/IrelandEnsDeclarationErrors';
import { ShowRawDataDiv, StickyDrawerHeader } from '../../DeclarationsNotificationsDrawer.styles';
import RawMessage from '../../RawMessage';
import config from 'config/config';
import transformCdsErrorField from './transformCdsErrorField';
import getCdsErrorCards from './getCdsErrorCards';
import DeclarationErrorCards from '../error-cards/DeclarationErrorCards';
import groupErrorCardsByItem from '../error-cards/groupErrorCardsByItem';
import { CenteredSpin } from '../error-cards/DeclarationErrorCard.styles';
import useErrorCardContext from '../error-cards/useErrorCardContext';
import useGoToErrorField from '../useGoToErrorField';
import formatErrorField from '../error-cards/formatErrorField';

const { Text, Title, Link } = Typography;

interface Props extends SpecificDeclarationErrorsProps {
    onClose?: () => void;
}

export const NotificationHeader = ({
    title,
    columnsStyle,
    columns,
}: {
    title?: string;
    columnsStyle?: string;
    columns?: string[];
}) => {
    return (
        <MainGrid columns={columnsStyle}>
            {columns ? (
                columns.map((column, index) => (
                    <Text strong key={`${column}-${index}`}>
                        {column}
                    </Text>
                ))
            ) : (
                <>
                    <Text strong>{title ?? 'CDS Error Code'}</Text>
                    <Text strong>Description</Text>
                    <Text strong></Text>
                </>
            )}
        </MainGrid>
    );
};

const ErrorsList = ({
    errors,
    error,
    onShowDetails,
    additionalInformation,
}: {
    errors?: CdsExportNotificationError[];
    error?: CdsExportNotificationError | null;
    onShowDetails?: (error: CdsExportNotificationError) => void;
    additionalInformation?: CdsExportNotificationAdditionalInformation[];
}) => {
    if (error) {
        const { code, message } = error;
        return (
            <MainGrid>
                <Error>{code ?? 'n/a'}</Error>
                <Text>{message ?? 'n/a'}</Text>
                {!error ? <ButtonText onClick={() => onShowDetails?.(error)}>Show details</ButtonText> : <span></span>}
            </MainGrid>
        );
    }

    const groupedErrors = groupBy(errors, 'groupId');
    const groupedAdditionalInformation = groupBy(additionalInformation, ({ field }) => field.split('.').at(3));

    return (
        <>
            {Object.entries(groupedErrors)?.map(([groupNumber, _errors], i) => {
                return (
                    <div>
                        <Text style={{ fontSize: '2rem' }} strong>
                            Group {groupNumber}
                        </Text>
                        {_errors.map((_error) => {
                            const { code, message } = _error;
                            return (
                                <MainGrid>
                                    <Error>{code ?? 'n/a'}</Error>
                                    <Text>{message ?? 'n/a'}</Text>
                                    {!error ? (
                                        <div>
                                            <ButtonText onClick={() => onShowDetails?.(_error)}>
                                                Show details
                                            </ButtonText>
                                        </div>
                                    ) : (
                                        <span></span>
                                    )}
                                </MainGrid>
                            );
                        })}
                        <AdditionalInformationList items={groupedAdditionalInformation[groupNumber]} />
                    </div>
                );
            })}
        </>
    );
};

const AdditionalInformationList = ({ items }: { items?: CdsExportNotificationAdditionalInformation[] }) => {
    if (!items?.length) return null;

    return (
        <div>
            <Text strong style={{ fontSize: '1.75rem' }}>
                Additional Information
            </Text>
            <div style={{ display: 'grid', gap: '1rem', marginTop: '0.5rem' }}>
                {items.map(({ field, statementDescription, statementCode, statementTypeCode }) => (
                    <div>
                        <div>
                            <Text strong>Field: </Text>
                            <Text>{field ?? '-'}</Text>
                        </div>
                        <div>
                            <Text strong>Code: </Text>
                            <Text>{statementCode ?? '-'}</Text>
                        </div>
                        <div>
                            <Text strong>Type Code: </Text>
                            <Text>{statementTypeCode ?? '-'}</Text>
                        </div>
                        <div>
                            <Text strong>Description: </Text>
                            <Text>{statementDescription ?? '-'}</Text>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// The form names the dangerous goods and agent fields differently from CDS.
const toCdsFormFieldName = (formPath: string) => {
    if (formPath.includes('dangerousGoods')) return transformDangerousGoodsPath(formPath);
    if (formPath.includes('agent')) return transformAgentPath(formPath);
    return formPath;
};

const excludedFields: string[] = ['amendment'];
const isFieldNotExcluded = (field: string) => !excludedFields.includes(field);

interface DetailedErrorProps {
    error: CdsExportNotificationError | null;
    onBack: () => void;
    onGoToField?: () => void;
    errorsRender: ReactNode;
    /** The additional information CDS sent for the error's group. */
    additionalInformation?: CdsExportNotificationAdditionalInformation[];
    /** The field as the error's card names it - with the item by its number today. */
    fieldLabel?: string;
}
const DetailedError = ({
    error,
    onBack: handleBack,
    onGoToField: handleGoToField,
    errorsRender,
    additionalInformation,
    fieldLabel,
}: DetailedErrorProps) => {
    const uiField = useMemo(() => {
        if (fieldLabel) return fieldLabel;

        // Previous errors answer earlier submissions, which have no card to name their items.
        const field = transformCdsErrorField(error?.field);
        const _uiField = field && getFieldName(parseValidationErrors, field, 'governmentAgencyGoodsItem');
        if (field && (!_uiField || _uiField === 'Unknown card - Unknown field')) return field;
        if (!field) return error?.field;
        return _uiField;
    }, [error?.field, fieldLabel]);
    return (
        <>
            <StickyDrawerHeader>
                <Button onClick={handleBack}>Back to list</Button>
            </StickyDrawerHeader>
            <Title level={3} style={{ marginTop: '2rem', fontWeight: 700 }}>
                Error details
            </Title>
            <DetailsGrid style={{ marginTop: '2rem' }}>
                <Text strong>Field:</Text>
                {uiField && isFieldNotExcluded(uiField) ? (
                    <Typography.Link onClick={handleGoToField}>{uiField}</Typography.Link>
                ) : (
                    <Text>{uiField || 'n/a'}</Text>
                )}
            </DetailsGrid>
            <Divider />
            <>
                <NotificationHeader />
                {errorsRender}
            </>
            <div style={{ marginTop: '2rem', display: 'grid' }}>
                <Text strong>CDS Error Explanation</Text>
                <Text>{error?.explanation ?? 'n/a'}</Text>
            </div>
            <div style={{ marginTop: '2rem' }}>
                <AdditionalInformationList items={additionalInformation} />
            </div>
            <Link
                style={{ marginTop: '5rem', display: 'inline-block' }}
                href="https://view.officeapps.live.com/op/view.aspx?src=https:%2f%2fassets.publishing.service.gov.uk%2fgovernment%2fuploads%2fsystem%2fuploads%2fattachment_data%2ffile%2f1103977%2fcds_error_code_list-enhanced.ods&wdOrigin=BROWSELINK"
                target="_blank"
            >
                CDS Error Code List
            </Link>
        </>
    );
};

interface PreviousErrorsProps {
    notifications?: CdsExportNotification[];
}
const PreviousErrors = ({ notifications }: PreviousErrorsProps): ReactElement | null => {
    const { declarationId } = useParams();
    const { declarationHistory, getDeclarationHistory } = useDeclarations();
    const lastDateOfSubmit = useMemo(() => getLastDateOfSubmit(declarationHistory), [declarationHistory]);
    const [error, setError] = useState<CdsExportNotificationError | null>(null);

    useEffect(() => {
        if (declarationId) getDeclarationHistory(declarationId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const previousNotificationsWithErrors = useMemo(() => {
        const _notifications = notifications && [...notifications];
        _notifications?.pop();
        _notifications?.reverse();
        return _notifications?.filter(
            (notification) =>
                lastDateOfSubmit &&
                notification?.issueDateTime &&
                !isEmpty(notification.validationMessages) &&
                lastDateOfSubmit < notification?.issueDateTime
        );
    }, [notifications, lastDateOfSubmit]);

    const showDetails = (error: CdsExportNotificationError) => {
        setError(error);
    };

    const defaultActiveKey = useMemo(() => {
        return Array(previousNotificationsWithErrors?.length)
            .fill(null)
            .map((_, index) => index);
    }, [previousNotificationsWithErrors?.length]);

    const notificationsList = useMemo(() => {
        if (!previousNotificationsWithErrors || !previousNotificationsWithErrors.length) return 'No previous errors';
        return (
            <Collapse defaultActiveKey={defaultActiveKey}>
                {previousNotificationsWithErrors?.map((notification, index) => {
                    return (
                        <Collapse.Panel
                            header={new Date(notification.issueDateTime ?? '').toLocaleString()}
                            key={index}
                        >
                            {error ? (
                                <DetailedError
                                    error={error}
                                    onBack={() => setError(null)}
                                    errorsRender={<ErrorsList error={error} />}
                                />
                            ) : (
                                <>
                                    <NotificationHeader />
                                    <ErrorsList errors={notification.validationMessages} onShowDetails={showDetails} />
                                </>
                            )}
                        </Collapse.Panel>
                    );
                })}
            </Collapse>
        );
    }, [defaultActiveKey, error, previousNotificationsWithErrors]);

    if (isEmpty(notificationsList)) return null;

    return (
        <Collapse defaultActiveKey="1">
            <Collapse.Panel header="Previous errors" key={1}>
                {notificationsList}
            </Collapse.Panel>
        </Collapse>
    );
};

const UkExportDeclarationErrors = ({
    errors,
    additionalInformation,
    onClose,
    notifications,
    latestNotification,
    showRawData: showRawDataProp = false,
}: Props) => {
    const [error, setError] = useState<CdsExportNotificationError | null>(null);
    const [showRawData, setShowRawData] = useState<boolean>(showRawDataProp);

    const { context, isLoaded } = useErrorCardContext();
    const goToErrorField = useGoToErrorField({
        onClose,
        toFormFieldName: toCdsFormFieldName,
        box44Paths: context?.box44Paths,
    });

    const cards = useMemo(() => (context ? getCdsErrorCards(errors, context) : []), [errors, context]);

    const showDetails = (error: CdsExportNotificationError) => {
        setError(error);
    };
    const goBack = () => {
        setError(null);
    };
    const hasSelectedError = useMemo(() => !!error, [error]);

    // The details view names and links the field the way the error's card does - the item found by id.
    const selectedField = error && context ? getCdsErrorCards([error], context)[0]?.fields[0] : undefined;
    const handleGoToField = () => {
        if (selectedField) goToErrorField(selectedField);
    };

    if (hasSelectedError) {
        return (
            <DetailedError
                error={error}
                onBack={goBack}
                onGoToField={handleGoToField}
                errorsRender={<ErrorsList error={error} />}
                additionalInformation={additionalInformation?.filter(
                    ({ field }) => field.split('.').at(3) === String(error?.groupId)
                )}
                fieldLabel={selectedField && formatErrorField(selectedField)}
            />
        );
    }

    if (!!showRawData) {
        return (
            <>
                {!showRawDataProp && (
                    <StickyDrawerHeader>
                        <Button onClick={() => setShowRawData(false)}>Back to list</Button>
                    </StickyDrawerHeader>
                )}
                <Title level={3} style={{ marginTop: '2rem', fontWeight: 700 }}>
                    Error details
                </Title>
                <RawMessage>{latestNotification?.rawMessage}</RawMessage>
            </>
        );
    }

    return (
        <>
            {config.isPIT && (
                <ShowRawDataDiv>
                    <ButtonText onClick={() => setShowRawData(true)}>Show raw data</ButtonText>
                </ShowRawDataDiv>
            )}

            {!isLoaded ? (
                <CenteredSpin>
                    <Spin />
                </CenteredSpin>
            ) : context ? (
                <DeclarationErrorCards
                    sections={groupErrorCardsByItem(cards)}
                    onGoToField={goToErrorField}
                    onShowDetails={showDetails}
                />
            ) : (
                <>
                    <NotificationHeader />
                    <ErrorsList
                        errors={errors}
                        additionalInformation={additionalInformation}
                        onShowDetails={showDetails}
                    />
                </>
            )}
        </>
    );
};

export default UkExportDeclarationErrors;
